import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusCircleFilled,
} from '@ant-design/icons';
import {
  EWithdrawalChannel,
  EWithdrawalStatus,
  type IAdminWithdrawalListItem,
} from '@mahawi/eshop-common/src/types';
import { Input, Select, Tag, Tooltip } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import LinkButton from '../components/link-button';
import StatusTag from '../components/status-tag';
import TableComponent from '../components/table';
import {
  WITHDRAWAL_CHANNEL_LABEL,
  WITHDRAWAL_STATUS_LABEL,
} from '../components/withdrawal/labels';
import RefundDue from '../components/withdrawal/refund-due';
import WithdrawalStatusTag from '../components/withdrawal/status-tag';
import { EDateFormat } from '../fragments/date-format';
import { parseList } from '../fragments/parse-list';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { withdrawalsLoad } from '../reducers/withdrawal/reducer';
import { type IWithdrawalsFilter } from '../reducers/withdrawal/types';
import { COLOR } from '../theme/tokens';

enum EWithdrawalSearchParam {
  STATUS = 'status',
  CHANNEL = 'channel',
  ASSIGNMENT = 'assignment',
  QUERY = 'query',
}

enum EWithdrawalAssignmentFilter {
  ALL = 'all',
  NEEDS_MANUAL = 'needsManual',
  LINKED = 'linked',
}

const ASSIGNMENT_OPTIONS = [
  { value: EWithdrawalAssignmentFilter.ALL, label: 'All' },
  {
    value: EWithdrawalAssignmentFilter.NEEDS_MANUAL,
    label: 'Needs manual assignment',
  },
  { value: EWithdrawalAssignmentFilter.LINKED, label: 'Linked to an order' },
];

const STATUS_OPTIONS = Object.values(EWithdrawalStatus).map(
  (status: EWithdrawalStatus) => ({
    value: status,
    label: WITHDRAWAL_STATUS_LABEL[status],
  }),
);

const CHANNEL_OPTIONS = Object.values(EWithdrawalChannel).map(
  (channel: EWithdrawalChannel) => ({
    value: channel,
    label: WITHDRAWAL_CHANNEL_LABEL[channel],
  }),
);

type TWithdrawalRow = IAdminWithdrawalListItem & { key: string };

const parseWithdrawalsFilter = (
  searchParams: URLSearchParams,
): IWithdrawalsFilter => {
  const statuses: string[] = parseList(
    searchParams.get(EWithdrawalSearchParam.STATUS),
  );
  const channels: string[] = parseList(
    searchParams.get(EWithdrawalSearchParam.CHANNEL),
  );
  const assignment: string | null = searchParams.get(
    EWithdrawalSearchParam.ASSIGNMENT,
  );
  const query: string = (
    searchParams.get(EWithdrawalSearchParam.QUERY) ?? ''
  ).trim();

  let needsManualAssignment: boolean | null = null;

  if (assignment === EWithdrawalAssignmentFilter.NEEDS_MANUAL) {
    needsManualAssignment = true;
  } else if (assignment === EWithdrawalAssignmentFilter.LINKED) {
    needsManualAssignment = false;
  }

  return {
    status: Object.values(EWithdrawalStatus).filter(
      (status: EWithdrawalStatus) => statuses.includes(status),
    ),
    channel: Object.values(EWithdrawalChannel).filter(
      (channel: EWithdrawalChannel) => channels.includes(channel),
    ),
    needsManualAssignment,
    query: query || null,
  };
};

const toAssignmentFilter = (
  needsManualAssignment: boolean | null,
): EWithdrawalAssignmentFilter => {
  if (needsManualAssignment === true) {
    return EWithdrawalAssignmentFilter.NEEDS_MANUAL;
  }

  if (needsManualAssignment === false) {
    return EWithdrawalAssignmentFilter.LINKED;
  }

  return EWithdrawalAssignmentFilter.ALL;
};

const columns: ColumnsType<TWithdrawalRow> = [
  {
    title: 'Received',
    dataIndex: 'receivedAt',
    render: (receivedAt: string): string =>
      dayjs(receivedAt).format(EDateFormat.DATE_TIME),
    sorter: (a: TWithdrawalRow, b: TWithdrawalRow): number =>
      dayjs(a.receivedAt).unix() - dayjs(b.receivedAt).unix(),
    defaultSortOrder: 'descend',
  },
  {
    title: 'Reference',
    dataIndex: 'referenceCode',
    ...getColumnSearch<TWithdrawalRow>('referenceCode'),
    render: (
      referenceCode: string,
      record: TWithdrawalRow,
    ): React.ReactElement => (
      <Link to={`/orders/withdrawal/${record.uuid}`} label={referenceCode} />
    ),
  },
  {
    title: 'Name',
    dataIndex: 'name',
    ...getColumnSearch<TWithdrawalRow>('name'),
  },
  {
    title: 'Order number',
    dataIndex: 'orderNumber',
    ...getColumnSearch<TWithdrawalRow>('orderNumber'),
  },
  {
    title: 'Order',
    render: (_value: string, record: TWithdrawalRow): React.ReactElement =>
      record.order ? (
        <Link
          to={`/order/${record.order.uuid}`}
          label={record.order.shortCode}
        />
      ) : (
        <Tag color="orange">Needs manual assignment</Tag>
      ),
  },
  {
    title: 'Channel',
    dataIndex: 'channel',
    render: (channel: EWithdrawalChannel): React.ReactElement => (
      <StatusTag status={channel} label={WITHDRAWAL_CHANNEL_LABEL[channel]} />
    ),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (status: EWithdrawalStatus): React.ReactElement => (
      <WithdrawalStatusTag status={status} />
    ),
  },
  {
    title: 'Refunded',
    align: 'right',
    render: (_value: string, record: TWithdrawalRow): string =>
      record.refundedAmount === null
        ? ''
        : `${record.refundedAmount} ${record.currencyIsoCode ?? ''}`.trim(),
  },
  {
    title: 'Confirmation',
    dataIndex: 'confirmationSent',
    align: 'center',
    render: (confirmationSent: boolean): React.ReactElement =>
      confirmationSent ? (
        <Tooltip title="Confirmation email sent">
          <CheckCircleOutlined style={{ color: COLOR.success }} />
        </Tooltip>
      ) : (
        <Tooltip title="Confirmation email not sent yet">
          <CloseCircleOutlined style={{ color: COLOR.error }} />
        </Tooltip>
      ),
  },
  {
    title: 'Refund due',
    dataIndex: 'refundDueAt',
    render: (_value: string, record: TWithdrawalRow): React.ReactElement => (
      <RefundDue status={record.status} refundDueAt={record.refundDueAt} />
    ),
    sorter: (a: TWithdrawalRow, b: TWithdrawalRow): number =>
      dayjs(a.refundDueAt).unix() - dayjs(b.refundDueAt).unix(),
  },
];

function WithdrawalsContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const withdrawals = useSelector((s: RootState) => s.Withdrawal.list);
  const inProcess = useSelector((s: RootState) => s.Withdrawal.inProcess);
  const [searchParams, setSearchParams] = useSearchParams();

  const filter: IWithdrawalsFilter = parseWithdrawalsFilter(searchParams);
  const filterKey: string = JSON.stringify(filter);

  useEffect((): void => {
    dispatch(withdrawalsLoad(filter));
  }, [dispatch, filterKey]);

  const setParam = (key: EWithdrawalSearchParam, value: string): void => {
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }

    searchParams.delete('page');
    setSearchParams(searchParams);
  };

  const dataSource = useMemo<TWithdrawalRow[]>(
    () =>
      (withdrawals ?? []).map((withdrawal: IAdminWithdrawalListItem) => ({
        key: withdrawal.uuid,
        ...withdrawal,
      })),
    [withdrawals],
  );

  return (
    <>
      <DetailPageHeader
        title="Withdrawals"
        onReload={(): void => {
          dispatch(withdrawalsLoad(filter));
        }}
        isReloadDisabled={inProcess}
        actions={
          <>
            <Select
              mode="multiple"
              allowClear
              placeholder="Status"
              style={{ minWidth: 200 }}
              options={STATUS_OPTIONS}
              value={filter.status}
              onChange={(values: EWithdrawalStatus[]): void => {
                setParam(EWithdrawalSearchParam.STATUS, values.join(','));
              }}
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="Channel"
              style={{ minWidth: 200 }}
              options={CHANNEL_OPTIONS}
              value={filter.channel}
              onChange={(values: EWithdrawalChannel[]): void => {
                setParam(EWithdrawalSearchParam.CHANNEL, values.join(','));
              }}
            />
            <Select
              style={{ width: 220 }}
              options={ASSIGNMENT_OPTIONS}
              value={toAssignmentFilter(filter.needsManualAssignment)}
              onChange={(value: EWithdrawalAssignmentFilter): void => {
                setParam(
                  EWithdrawalSearchParam.ASSIGNMENT,
                  value === EWithdrawalAssignmentFilter.ALL ? '' : value,
                );
              }}
            />
            <Input.Search
              allowClear
              placeholder="Name, email, order number or reference"
              defaultValue={filter.query ?? ''}
              style={{ width: 320 }}
              onSearch={(value: string): void => {
                setParam(EWithdrawalSearchParam.QUERY, value.trim());
              }}
            />
            <LinkButton
              to="/orders/withdrawal/new"
              label="New withdrawal"
              type="primary"
              icon={<PlusCircleFilled />}
            />
          </>
        }
      />
      <TableComponent
        columns={columns as Parameters<typeof TableComponent>[0]['columns']}
        dataSource={dataSource}
        isLoading={inProcess}
        emptyText="No withdrawals match the filter"
      />
    </>
  );
}

export default WithdrawalsContainer;
